import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { 
  Lock, Zap, Crown, ArrowRight, Sparkles, 
  BarChart3, Mail, Palette, Trophy, Calendar, Headphones
} from "lucide-react";
import { useFeatureGate, Features, TierInfo } from "../contexts/FeatureGateContext";

// Feature icons mapping
const featureIcons = {
  [Features.AI_FEATURES]: Sparkles,
  [Features.EMAIL_NOTIFICATIONS]: Mail,
  [Features.CUSTOM_BRANDING]: Palette,
  [Features.TOURNAMENT_MANAGEMENT]: Trophy,
  [Features.LEAGUE_MANAGEMENT]: Calendar,
  [Features.PRIORITY_SUPPORT]: Headphones,
};

// Feature display names
const featureNames = {
  [Features.AI_FEATURES]: "AI Features",
  [Features.EMAIL_NOTIFICATIONS]: "Email Notifications",
  [Features.CUSTOM_BRANDING]: "Custom Branding",
  [Features.TOURNAMENT_MANAGEMENT]: "Tournament Management",
  [Features.LEAGUE_MANAGEMENT]: "League Management",
  [Features.PRIORITY_SUPPORT]: "Priority Support",
};

// Which tier unlocks which feature — all features available on Free, priority support on Pro
const featureTiers = {
  [Features.AI_FEATURES]: "free",
  [Features.EMAIL_NOTIFICATIONS]: "free",
  [Features.CUSTOM_BRANDING]: "free",
  [Features.TOURNAMENT_MANAGEMENT]: "free",
  [Features.LEAGUE_MANAGEMENT]: "free",
  [Features.PRIORITY_SUPPORT]: "pro",
};

/**
 * UpgradePrompt - Shows when a feature is locked
 */
export const UpgradePrompt = ({ 
  feature, 
  title, 
  description, 
  compact = false,
  className = "" 
}) => {
  const navigate = useNavigate();
  const { getTier } = useFeatureGate();
  const currentTier = getTier();
  
  const Icon = featureIcons[feature] || Lock;
  const displayName = title || featureNames[feature] || "This feature";
  const requiredTier = featureTiers[feature] || "plus";
  const requiredTierInfo = TierInfo[requiredTier];

  if (compact) {
    return (
      <div className={`flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl ${className}`}>
        <Lock className="w-4 h-4 text-slate-400" />
        <span className="text-sm text-slate-600">
          {displayName} requires{" "}
          <span className={`font-medium ${requiredTier === "premium" ? "text-amber-600" : "text-violet-600"}`}>
            {requiredTierInfo.name}
          </span>
        </span>
        <Button 
          size="sm" 
          variant="ghost"
          onClick={() => navigate("/admin/settings?tab=billing")}
          className="ml-auto text-xs"
        >
          Upgrade
        </Button>
      </div>
    );
  }

  return (
    <Card className={`border-dashed border-2 border-slate-200 bg-slate-50/50 ${className}`}>
      <CardContent className="py-8 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center">
          <Icon className="w-7 h-7 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          {displayName}
        </h3>
        <p className="text-slate-500 mb-6 max-w-md mx-auto">
          {description || `Unlock ${displayName.toLowerCase()} by upgrading to ${requiredTierInfo.name}`}
        </p>
        <div className="flex flex-col items-center gap-3">
          <Button 
            onClick={() => navigate("/admin/settings?tab=billing")}
            className={`rounded-xl px-6 ${
              requiredTier === "premium" 
                ? "bg-amber-500 hover:bg-amber-600" 
                : "bg-violet-600 hover:bg-violet-700"
            }`}
          >
            {requiredTier === "premium" ? (
              <Crown className="w-4 h-4 mr-2" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Upgrade to {requiredTierInfo.name}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-xs text-slate-400">
            Currently on {TierInfo[currentTier]?.name || "Free"} plan
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * LimitReachedPrompt - Shows when a limit is reached
 */
export const LimitReachedPrompt = ({ 
  limitType, 
  current, 
  limit, 
  itemName = "items",
  className = "" 
}) => {
  const navigate = useNavigate();
  const { getTier } = useFeatureGate();
  const currentTier = getTier();
  
  const nextTier = currentTier === "free" ? "plus" : "premium";
  const nextTierInfo = TierInfo[nextTier];

  return (
    <Card className={`border-amber-200 bg-amber-50 ${className}`}>
      <CardContent className="py-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <BarChart3 className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-amber-900 mb-1">
              {itemName} Limit Reached
            </h4>
            <p className="text-sm text-amber-700 mb-3">
              You&apos;ve used {current} of {limit} {itemName.toLowerCase()} on your {TierInfo[currentTier]?.name} plan.
              Upgrade to add more.
            </p>
            <Button 
              size="sm"
              onClick={() => navigate("/admin/settings?tab=billing")}
              className={`rounded-lg ${
                nextTier === "premium" 
                  ? "bg-amber-500 hover:bg-amber-600" 
                  : "bg-violet-600 hover:bg-violet-700"
              }`}
            >
              {nextTier === "premium" ? (
                <Crown className="w-4 h-4 mr-2" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Upgrade to {nextTierInfo.name}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * FeatureGate - Wrapper component that shows content or upgrade prompt
 */
export const FeatureGate = ({ 
  feature, 
  children, 
  fallback = null,
  showPrompt = true 
}) => {
  const { hasFeature, loading, features } = useFeatureGate();
  
  // While loading features, show the content (optimistic rendering)
  // This prevents the "upgrade" flash while features are being fetched
  if (loading || !features) {
    return children;
  }
  
  if (hasFeature(feature)) {
    return children;
  }
  
  if (showPrompt) {
    return <UpgradePrompt feature={feature} />;
  }
  
  return fallback;
};

/**
 * UsageBadge - Shows current usage vs limit
 */
export const UsageBadge = ({ limitType, label }) => {
  const { getUsage } = useFeatureGate();
  const usage = getUsage(limitType);
  
  if (usage.unlimited) {
    return (
      <span className="text-xs text-slate-500">
        {usage.current} {label} (Unlimited)
      </span>
    );
  }
  
  const percentage = (usage.current / usage.limit) * 100;
  const isNearLimit = percentage >= 80;
  const isAtLimit = percentage >= 100;
  
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all ${
            isAtLimit ? "bg-red-500" : isNearLimit ? "bg-amber-500" : "bg-emerald-500"
          }`}
          style={{ width: `${Math.min(100, percentage)}%` }}
        />
      </div>
      <span className={`text-xs ${isAtLimit ? "text-red-600" : isNearLimit ? "text-amber-600" : "text-slate-500"}`}>
        {usage.current}/{usage.limit} {label}
      </span>
    </div>
  );
};

/**
 * TierBadge - Shows the current subscription tier
 */
export const TierBadge = ({ tier, size = "sm" }) => {
  const tierInfo = TierInfo[tier] || TierInfo.free;
  
  const sizeClasses = {
    xs: "px-1.5 py-0.5 text-[10px]",
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-1.5 text-sm",
  };
  
  return (
    <span className={`inline-flex items-center gap-1 font-medium rounded-lg ${tierInfo.color} ${sizeClasses[size]}`}>
      {(tier === "pro" || tier === "plus" || tier === "premium") && <Zap className="w-3 h-3" />}
      {tierInfo.name}
    </span>
  );
};

export default FeatureGate;
